import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from '@shigo-ui/ui';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  Label,
  TagInput,
} from '@shigo-ui/ui';

import { useBusyDialogGuard } from '../../../../../hooks/useBusyDialogGuard';
import { dashboardT } from '../../../../../lib/messages';
import { assetTagsFormSchema, type AssetTagsFormValue } from '../../assetFormSchema';
import { useAssetMutations } from '../../hooks';
import { A2vAssetType, type A2vAsset } from '../../model';

interface AssetEditDialogProps {
  asset: A2vAsset;
  onClose: () => void;
}

export function AssetEditDialog({ asset, onClose }: AssetEditDialogProps) {
  'use no memo';
  const mutations = useAssetMutations();
  const form = useForm<AssetTagsFormValue>({
    resolver: zodResolver(assetTagsFormSchema),
    defaultValues: { tags: asset.tags },
  });
  useEffect(() => {
    form.reset({ tags: asset.tags });
  }, [asset, form]);
  const pending = mutations.update.isPending;
  const guard = useBusyDialogGuard(pending, onClose);

  async function submit(values: AssetTagsFormValue) {
    try {
      await mutations.update.mutateAsync({ asset, tags: values.tags });
      toast.success(dashboardT('a2v.assets.edit.success'));
      onClose();
    } catch {
      form.setError('root.server', { message: dashboardT('a2v.assets.edit.error') });
    }
  }

  return (
    <Dialog open onOpenChange={guard.onOpenChange}>
      <DialogContent className="max-w-[700px] gap-0 overflow-hidden p-0">
        <DialogHeader className="border-border border-b px-5 py-3.5">
          <DialogTitle className="text-[15px] font-semibold">
            {dashboardT('a2v.assets.edit.title')}
          </DialogTitle>
          <DialogDescription>{dashboardT('a2v.assets.edit.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={(event) => void form.handleSubmit(submit)(event)} noValidate>
            <div className="max-h-[70vh] space-y-5 overflow-y-auto px-5 py-4">
              <div className="border-input bg-muted/20 overflow-hidden rounded-md border">
                <div className="bg-muted flex aspect-video items-center justify-center overflow-hidden">
                  {asset.assetType === A2vAssetType.Video ? (
                    <video className="h-full w-full object-contain" src={asset.url} controls>
                      <track kind="captions" />
                    </video>
                  ) : (
                    <img
                      className="h-full w-full object-contain"
                      src={asset.url}
                      alt={asset.name}
                    />
                  )}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
                  <span className="min-w-0 truncate text-sm font-medium">{asset.name}</span>
                  <span className="text-muted-foreground text-xs">
                    {String(asset.width)} x {String(asset.height)}
                  </span>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[13px] font-semibold">{dashboardT('a2v.assets.tags')}</Label>
                <Controller
                  control={form.control}
                  name="tags"
                  render={({ field }) => (
                    <TagInput
                      label={dashboardT('a2v.assets.tags')}
                      value={field.value}
                      onValueChange={field.onChange}
                      placeholder={dashboardT('a2v.assets.edit.tagsPlaceholder')}
                      disabled={pending}
                    />
                  )}
                />
              </div>
              {form.formState.errors.root?.server?.message ? (
                <p role="alert" className="text-destructive text-sm">
                  {form.formState.errors.root.server.message}
                </p>
              ) : null}
            </div>
            <DialogFooter className="border-border border-t px-5 py-3">
              <Button type="button" variant="outline" disabled={pending} onClick={onClose}>
                {dashboardT('a2v.assets.cancel')}
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? dashboardT('a2v.assets.saving') : dashboardT('a2v.assets.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
